import React, { useEffect, useRef, useState } from 'react';
import { Masonry } from 'react-masonry'
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  Button,
  IconClose,
  IconViewGallery,
  IconButtonArrow,
  IconCarouselArrows
} from '../../components';
import cmsCss from '../CMSPageCarousel/CMSPageCarousel.module.css';

import css from './MasonryPopup.module.css';

const MODAL_BREAKPOINT = 768;

const MASONRY_VARIANTS = [
  'scaled-small',
  'scaled-medium',
  'scaled-large',
  'scaled-xlarge',
];

const pickVariant = (img, index) => {
  const variants = img?.attributes?.variants || {};
  const seed =
    img.id.uuid
      .split('')
      .reduce((acc, c) => acc + c.charCodeAt(0), 0) + index;

  const variantName = MASONRY_VARIANTS[seed % MASONRY_VARIANTS.length];

  return variants[variantName] || variants['scaled-large'];
};

const isSharetribeImage = (item) => {
  return item && item.id && item.id.uuid && item.attributes && item.attributes.variants;
};

const isReactElement = (item) => {
  return item && React.isValidElement(item);
};

const extractMediaFromReactElement = (element) => {
  if (!React.isValidElement(element)) return null;

  const mediaContainer = element.props.children?.find?.(child =>
    React.isValidElement(child) && child.props.className === cmsCss.mediaContainer
  );

  if (!mediaContainer) return null;

  const mediaContent = mediaContainer.props.children;

  if (React.isValidElement(mediaContent)) {
    if (mediaContent.type === 'img') {
      return {
        type: 'image',
        src: mediaContent.props.src,
        alt: mediaContent.props.alt,
        className: mediaContent.props.className
      };
    } else if (mediaContent.type === 'div' && mediaContent.props.className === cmsCss.videoContainer) {
      const iframe = mediaContent.props.children;
      if (React.isValidElement(iframe) && iframe.type === 'iframe') {
        return {
          type: 'video',
          src: iframe.props.src,
          title: iframe.props.title,
          className: iframe.props.className
        };
      }
    }
  }

  return null;
};

const MasonryPopup = props => {

  const {
    id,
    items,
    buttonClass,
    buttonText,
    iconType,
    onManageDisableScrolling,
    listingTitle,
    hideTopbar
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [carouselImageIndex, setCarouselImageIndex] = useState(null);

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const masonryRef = useRef(null);

  const handleImageLoad = (imageKey) => {
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageKey);
      return newSet;
    });
  };

  const handleOpenImageCarousel = (index) => {
    setIsOpen(false)
    setIsCarouselOpen(true)
    setCarouselImageIndex(index);
  }

  const handleCloseImageCarousel = (index) => {
    setIsOpen(false)
    setIsCarouselOpen(false)
    setCarouselImageIndex(null);
    onManageDisableScrolling(id, false);
  }

  // Reset loaded images when popup opens
  useEffect(() => {
    if (isOpen) {
      setImagesLoaded(false);
      setLoadedImages(new Set());
    }
  }, [isOpen]);

  // Check if all images are loaded
  useEffect(() => {
    const totalImages = items.filter(item =>
      isSharetribeImage(item) || (isReactElement(item) && extractMediaFromReactElement(item)?.type === 'image')
    ).length;

    if (isOpen && loadedImages.size === totalImages && totalImages > 0) {
      setImagesLoaded(true);
      // Force masonry re-layout
      if (masonryRef.current) {
        const masonryInstance = masonryRef.current;
        if (masonryInstance && typeof masonryInstance.forcePack === 'function') {
          masonryInstance.forcePack();
        } else if (masonryInstance && typeof masonryInstance.relayout === 'function') {
          masonryInstance.relayout();
        }
      }
    }
  }, [loadedImages, isOpen, items.length]);

  const images = items.map((item, i) => {
    if (isSharetribeImage(item)) {
      // Use scaled-xlarge variant for better quality in carousel
      const variants = item?.attributes?.variants || {};
      const xlargeVariant = variants['scaled-xlarge'] || variants['scaled-large'] || pickVariant(item, i);
      if (!xlargeVariant?.url) return null;

      return (
        <img
          className={css.galleryItem}
          key={item.id.uuid}
          src={xlargeVariant.url}
          alt={`${id}-image`}
          onLoad={() => handleImageLoad(item.id.uuid)}
          onClick={() => handleOpenImageCarousel(i)}
        />
      );
    } else if (isReactElement(item)) {
      const mediaData = extractMediaFromReactElement(item);
      if (!mediaData) return null;

      if (mediaData.type === 'image') {
        return (
          <img
            className={css.galleryItem}
            key={`${id}-react-img-${i}`}
            src={mediaData.src}
            alt={mediaData.alt || `${id}-image`}
            onLoad={() => handleImageLoad(`${id}-react-img-${i}`)}
            onClick={() => handleOpenImageCarousel(i)}
          />
        );
      } else if (mediaData.type === 'video') {
        return (
          <iframe
            className={css.galleryItem}
            key={`${id}-react-video-${i}`}
            src={mediaData.src}
            title={mediaData.title || `${id}-video`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      }
    }

    return null;
  }).filter(Boolean);

  // Handle keyboard navigation for carousel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isCarouselOpen || images.length === 0) return;

      switch (e.key) {
        case 'ArrowLeft':
          setCarouselImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
          break;
        case 'ArrowRight':
          setCarouselImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
          break;
        case 'Escape':
          handleCloseImageCarousel();
          break;
        default:
          break;
      }
    };

    if (isCarouselOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCarouselOpen, images.length]);

  const isWindowDefined = typeof window !== 'undefined';
  const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;

  return (
    <div className={css.root}>

      <Button
        className={buttonClass}
        type="button"
        onClick={() => {
          setIsOpen(true);
          onManageDisableScrolling(id, true);
        }}
      >
        {iconType === "gallery" && <IconViewGallery />}
        {iconType === "arrow" && <IconButtonArrow />}
        {buttonText}
      </Button>

      {isOpen && (
        <div className={classNames(css.galleryPopup, hideTopbar && css.galleryPopupHiddenTopbar)}>
          <h2 className={css.galleryPopupTitle}>
            {listingTitle && (
              <FormattedMessage id="ListingPage.galleryTitle" values={{ listingTitle }} />
            )}
            <span
              className={css.galleryPopupClose}
              onClick={() => {
                setIsOpen(false);
                onManageDisableScrolling(id, false);
              }}
            >
              <IconClose />
            </span>
          </h2>
          <div className={classNames(css.gallery, !imagesLoaded && css.loading)}>
            <Masonry ref={masonryRef}>
              {images}
            </Masonry>
          </div>
        </div>
      )}

      {isCarouselOpen && (
        <div className={classNames(css.galleryPopup, hideTopbar && css.galleryPopupHiddenTopbar)}>
          <h2 className={css.galleryPopupTitle}>
            {listingTitle && (
              <FormattedMessage id="ListingPage.galleryTitle" values={{ listingTitle }} />
            )}
            <span
              className={css.galleryPopupClose}
              onClick={() => handleCloseImageCarousel()}
            >
              <IconClose />
            </span>
          </h2>
          <div className={classNames(css.carousel, !imagesLoaded && css.loading)}>
            {carouselImageIndex !== null && images[carouselImageIndex]}
            {images.length > 1 && (
              <>
                <button
                  className={classNames(css.carouselNav, css.carouselPrev)}
                  onClick={() => setCarouselImageIndex(
                    carouselImageIndex === 0 ? images.length - 1 : carouselImageIndex - 1
                  )}
                >
                  <IconCarouselArrows type={"black-left"} />
                </button>
                <button
                  className={classNames(css.carouselNav, css.carouselNext)}
                  onClick={() => setCarouselImageIndex(
                    carouselImageIndex === images.length - 1 ? 0 : carouselImageIndex + 1
                  )}
                >
                  <IconCarouselArrows type={"black-right"} />
                </button>
              </>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default MasonryPopup;